"use client"

import PropTypes from "prop-types"
import { useCallback, useEffect, useRef } from "react"
import { createPortal } from "react-dom"
import { RiCloseLine } from "react-icons/ri"

import theme from "@/theme"
import { callFunctionIfDefined } from "@/utils/functions"

import { ModalContext } from "./provider"
import { CloseButton, Header, ModalBackground, ModalContent, Title } from "./styled"

/**
 * Modal component to display content on top of the current page
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The children of the Modal
 * @param {boolean} props.isOpen - If `true`, the Modal is open
 * @param {function} props.onClose - Function called to close the modal
 * @param {object} [props.backgroundProps] - The props to pass to the background component
 * @param {string} [props.title] - The title of the Modal
 * @param {"small"|"large"} [props.size] - The size of the modal
 * @param {string} [props.maxWidth] - Maximum width of the Modal
 * @param {function} [props.beforeOpen] - Function called before opening the modal
 * @param {string} [props.testId] - Test ID for automated testing
 * @returns {JSX.Element}
 */
export const Modal = ({
  children,
  isOpen,
  onClose,
  backgroundProps = {},
  title = "",
  size = "small",
  maxWidth,
  showCloseButton = true,
  testId,
  ...props
}) => {
  const node = useRef(null)

  // Handle Escape keydown
  useEffect(() => {
    const handleKeydown = (event) => {
      if (event.key === "Escape") {
        callFunctionIfDefined(onClose)
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleKeydown)
    }

    return () => {
      document.removeEventListener("keydown", handleKeydown)
    }
  }, [isOpen, onClose])

  const handleOnClick = (event) => {
    event.stopPropagation()
  }

  const handleOnClose = useCallback(() => {
    callFunctionIfDefined(onClose)
  }, [onClose])

  const handleOnCloseBackdrop = useCallback(
    (event) => {
      event.stopPropagation()

      if (node.current === event.target) {
        handleOnClose()
      }
    },
    [handleOnClose],
  )

  return (
    <ModalContext.Consumer>
      {({ modalNode }) =>
        modalNode && isOpen
          ? createPortal(
              <ModalBackground {...backgroundProps} onClick={(event) => handleOnCloseBackdrop(event)} ref={node}>
                <ModalContent
                  role="dialog"
                  onClick={(event) => handleOnClick(event)}
                  $size={size}
                  $maxWidth={maxWidth}
                  data-testid={testId}
                  {...props}
                >
                  <Header>
                    <Title>{title}</Title>
                    {showCloseButton && (
                      <CloseButton handleClick={handleOnClose}>
                        <RiCloseLine size={theme.iconSizes.large} />
                      </CloseButton>
                    )}
                  </Header>

                  {children}
                </ModalContent>
              </ModalBackground>,
              modalNode,
            )
          : null
      }
    </ModalContext.Consumer>
  )
}

Modal.propTypes = {
  /** The content of the Modal */
  children: PropTypes.node,
  /** If `true`, the Modal is open */
  isOpen: PropTypes.bool,
  /** Function called to close the modal */
  onClose: PropTypes.func,
  /** The props to pass to the background component */
  backgroundProps: PropTypes.object,
  /** The title of the Modal */
  title: PropTypes.string,
  /** The size of the modal */
  size: PropTypes.oneOf(["small", "large"]),
  /** Maximum width of the Modal */
  maxWidth: PropTypes.string,
  /** Test ID for automated testing */
  testId: PropTypes.string,
}
